import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Gauge } from './Gauge';
import { colourForDriverId } from '@/theme/tokens';
import type { AnnualStrategicDriver } from '@/types/models';
import { cn } from '@/lib/utils';

interface StrategicPulseBandProps {
  drivers: AnnualStrategicDriver[];
  /** Tenant-wide alignment percentage shown in the header. */
  alignmentPct?: number;
  className?: string;
}

interface DriverWithProgress extends AnnualStrategicDriver {
  /** Auto-computed completion 0–100. Null = unpopulated (driver shows as dash). */
  autoProgress: number | null;
}

function progressOf(driver: AnnualStrategicDriver): number | null {
  // Prefer the backend-computed auto-progress (% of linked work items
  // marked Done, populated when LinkedWorkItems are included). Fall back
  // to the user's manual progress slider (progressPercentage). Final
  // fallbacks cover legacy / experimental field names. Returns null
  // when nothing is populated so the gauge renders the dash empty state
  // rather than misleadingly showing 0%.
  const candidate = (driver as any).autoProgress
    ?? (driver as any).progressPercentage
    ?? (driver as any).progress
    ?? (driver as any).completionPct;
  if (candidate === null || candidate === undefined) return null;
  const n = Number(candidate);
  if (!Number.isFinite(n)) return null;
  // progressPercentage of 0 from a never-touched manual slider is
  // indistinguishable from "no data" — treat it as null so the gauge
  // shows the dash. autoProgress of 0 (real auto-computed value with
  // 0 items done out of N) survives this check because the previous
  // ?? chain already picked it; we only collapse 0-from-fallback.
  if (n === 0 && (driver as any).autoProgress == null) return null;
  return Math.max(0, Math.min(100, n));
}

// StrategicPulseBand — a full-width row of radial gauges, one per
// strategic driver, sorted high-to-low by auto-progress. Each driver
// renders in its own colour from the deterministic palette so a
// driver always looks the same across dashboard, projects, calendar.
// Unpopulated drivers show a dash and a muted track.
export function StrategicPulseBand({
  drivers,
  alignmentPct,
  className,
}: StrategicPulseBandProps) {
  const enriched: DriverWithProgress[] = drivers
    .map(d => ({ ...d, autoProgress: progressOf(d) }))
    .sort((a, b) => (b.autoProgress ?? -1) - (a.autoProgress ?? -1));

  return (
    <Card className={cn('w-full', className)}>
      <CardContent className="py-5">
        <div className="mb-4 flex items-baseline justify-between">
          <h3 className="text-base font-semibold">Strategic pulse</h3>
          {typeof alignmentPct === 'number' && (
            // Label was "of this week aligned" — misleading because the
            // metric is a status-based snapshot (% of live Planned /
            // InProgress items linked to ANY driver), not a weekly window.
            // High gauges + 0% alignment is a valid state: every linked
            // item is already Done, but current live work is unlinked.
            <span className="text-xs text-muted-foreground">
              {alignmentPct}% of live work aligned
            </span>
          )}
        </div>
        <div
          className="grid gap-4 gap-y-5"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(116px, 1fr))' }}
        >
          {enriched.map(driver => {
            const colour = driver.autoProgress === null
              ? 'hsl(var(--muted-foreground))'
              : colourForDriverId(driver.id);
            const label = driver.autoProgress === null ? '–' : `${Math.round(driver.autoProgress)}%`;
            return (
              <div key={driver.id} className="flex flex-col items-center gap-2 text-center">
                <div className="relative h-[84px] w-[84px]">
                  <Gauge value={driver.autoProgress ?? 0} colour={colour} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-mono text-base font-bold tracking-tight">{label}</span>
                  </div>
                </div>
                <div className="flex max-w-[130px] items-center justify-center gap-1.5 text-[11.5px] leading-tight text-muted-foreground">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ background: colour }}
                  />
                  <span className="line-clamp-2">{driver.title ?? 'Unnamed driver'}</span>
                </div>
              </div>
            );
          })}
        </div>
        {drivers.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No strategic drivers yet. Add a driver in Strategy to start the rhythm.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default StrategicPulseBand;
